import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/api";

const pronouns = ["She/Her", "He/Him", "They/Them", "Prefer not to say"];
const genders = ["Female", "Male", "Non-binary", "Other", "Prefer not to say"];

const Register = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    password: "",
    passwordConfirm: "",
    birthday: "",
    pronouns: "",
    gender: "",
    phone: "",
  });

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const {
      firstName,
      lastName,
      preferredName,
      email,
      password,
      passwordConfirm,
      birthday,
      pronouns,
      gender,
      phone,
    } = form;

    if (
      !firstName ||
      !lastName ||
      !preferredName ||
      !email ||
      !password ||
      !passwordConfirm
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (password !== passwordConfirm) {
      toast.error("Passwords do not match");
      return;
    }

    // Example API call
    try {
      const res = await api.post("/user/register", {
        firstName,
        lastName,
        preferredName,
        email,
        password,
        birthday,
        pronouns,
        gender,
        phone,
      });

      if (res.data.success) {
        toast.success("Registration successful! Please login.");
        navigate("/login");
      } else {
        toast.error(res.data.message || "Registration failed");
      }
    } catch (error) {
      toast.error("Server error, please try again later");
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-8">
        <h2
          className="text-4xl font-extrabold text-gray-900 mb-6"
          style={{ fontFamily: "Righteous, sans-serif" }}
        >
          Register
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                required
              />
            </div>
          </div>

          {/* Username */}
          <div>
            <label
              htmlFor="preferredName"
              className="block text-sm font-medium text-gray-700"
            >
              Username (Preferred Name) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="preferredName"
              name="preferredName"
              value={form.preferredName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
              required
              placeholder="Your preferred display name"
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
              required
              placeholder="you@example.com"
            />
          </div>

          {/* Password & Confirm */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                required
                minLength={6}
                placeholder="At least 6 characters"
              />
            </div>

            <div>
              <label
                htmlFor="passwordConfirm"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="passwordConfirm"
                name="passwordConfirm"
                value={form.passwordConfirm}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                required
                minLength={6}
                placeholder="Re-enter your password"
              />
            </div>
          </div>

          {/* Birthday, Pronoun, Gender */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="birthday"
                className="block text-sm font-medium text-gray-700"
              >
                Birthday
              </label>
              <input
                type="date"
                id="birthday"
                name="birthday"
                value={form.birthday}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                max={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div>
              <label
                htmlFor="pronouns"
                className="block text-sm font-medium text-gray-700"
              >
                Pronouns
              </label>
              <select
                id="pronouns"
                name="pronouns"
                value={form.pronouns}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
              >
                <option value="">Select pronouns</option>
                {pronouns.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700"
              >
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
              >
                <option value="">Select gender</option>
                {genders.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
              placeholder="+1 555 123 4567"
              pattern="^\+?[0-9\s\-]{7,15}$"
              title="Enter a valid phone number"
            />
          </div>

          {/* Submit */}
          <div className="w-full flex gap-4 justify-center">
            <button
              type="submit"
              className="px-12 mt-6 bg-gray-600 text-white py-3 rounded-md hover:bg-gray-700 transition duration-200"
            >
              Sing Up
            </button>

            <button
              onClick={() => navigate("/login")}
              className="px-12 mt-6 bg-gray-600 text-white py-3 rounded-md hover:bg-gray-700 transition duration-200"
            >
              Sing In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
